import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  Row,
  Col,
  InputNumber,
  Modal,
} from "antd";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import API from "../../../services/api";
import { useCloudinaryUpload } from "../../../hooks/useCloudinaryUpload";
import { useState, useEffect } from "react";
import "./AddCommercialSpace.css";
import { useNavigate } from "react-router-dom";
import LocationField from "../../LocationField";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PROPERTY_TYPES = [
  "office",
  "shop",
  "warehouse",
  "showroom",
  "coworking",
  "other",
];

// ===================== AMENITIES COMPONENT =====================
const QUICK_AMENITIES = [
  "Parking", "Lift", "Security", "Power Backup",
  "AC", "Wi-Fi", "CCTV", "Fire Safety",
  "Waste Disposal", "Pantry", "Meeting Rooms",
  "Reception", "Signage", "Loading Dock", "Ventilation"
];

const AmenitiesInput = ({ value = "", onChange, onBlur, message }) => {
  const [customInput, setCustomInput] = useState("");
  const [currentAmenities, setCurrentAmenities] = useState(() => {
    if (value && typeof value === "string") {
      return value.split(",").map(a => a.trim()).filter(Boolean);
    }
    return [];
  });

  const updateParent = (newList) => {
    const stringValue = newList.join(", ");
    setCurrentAmenities(newList);
    if (onChange) {
      onChange(stringValue);
    }

  };

  const addAmenity = (amenity) => {
    const normalized = amenity.trim();
    if (!normalized) return;
    if (currentAmenities.includes(normalized)) return;
    const newList = [...currentAmenities, normalized];
    updateParent(newList);
  };

  const removeAmenity = (amenity) => {
    const newList = currentAmenities.filter(a => a !== amenity);
    updateParent(newList);
  };

  const handleCustomAdd = () => {
    if (!customInput.trim()) {
      if (message) {
        message.error("Please enter a custom amenity");
      } else {
        alert("Please enter a custom amenity");
      }
      return;
    }
    addAmenity(customInput.trim());
    setCustomInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCustomAdd();
    }
  };


  return (
    <div className="csv2-amenities-wrapper">
      {/* QUICK ADD BUTTONS */}
      <div className="csv2-quick-add-section">
        <div className="csv2-quick-add-label">QUICK ADD AMENITIES:</div>
        <div className="csv2-quick-add-buttons">
          {QUICK_AMENITIES.map((amenity) => (
            <button
              key={amenity}
              type="button"
              className={`csv2-quick-btn ${currentAmenities.includes(amenity) ? "added" : ""}`}
              onClick={() => addAmenity(amenity)}
              disabled={currentAmenities.includes(amenity)}
            >
              {currentAmenities.includes(amenity) ? "✓" : "+"} {amenity}
            </button>
          ))}
        </div>
      </div>

      {/* CUSTOM AMENITIES INPUT */}
      <div className="csv2-custom-section">
        <div className="csv2-custom-label">Custom Amenities</div>
        <div className="csv2-custom-input-wrapper">
          <input
            type="text"
            className="csv2-custom-input"
            placeholder="e.g. Gym, Cafeteria, Rooftop Terrace..."
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button type="button" className="csv2-custom-add-btn" onClick={handleCustomAdd}>
            + Add
          </button>
        </div>
        <div className="csv2-custom-hint">Type a custom amenity and click Add</div>
      </div>

      {/* CURRENT AMENITIES LIST */}
      <div className="csv2-current-section">
        <div className="csv2-current-label">CURRENT AMENITIES:</div>
        {currentAmenities.length === 0 ? (
          <div className="csv2-empty-amenities">No amenities added yet. Click on Quick Add buttons or type custom amenities above.</div>
        ) : (
          <div className="csv2-current-list">
            {currentAmenities.map((amenity) => (
              <div key={amenity} className="csv2-amenity-tag">
                {amenity}
                <button
                  type="button"
                  className="csv2-remove-amenity"
                  onClick={() => removeAmenity(amenity)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
// ===================== END AMENITIES COMPONENT =====================

function AddCommercialSpace() {
  const [form] = Form.useForm();

  const propertyTypeValue = Form.useWatch("propertyType", form);
  const pricingTypeValue = Form.useWatch("pricingType", form);

  const { message } = App.useApp();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imagesError, setImagesError] = useState(false);
  const [customPropertyType, setCustomPropertyType] = useState("");
  const [showCustomPropertyType, setShowCustomPropertyType] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const {
    uploadedThumb,
    uploadedImages,
    imageFileList,
    setImageFileList,
    uploading,
    uploadThumbnail,
    uploadImages,
    removeImage
  } = useCloudinaryUpload("commercial_spaces", message);


  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });



  const handleBack = () => {
    const values = form?.getFieldsValue?.() || {};

    // Check if any field has value
    const hasData = Object.values(values).some(
      (val) =>
        val !== undefined &&
        val !== null &&
        val !== "" &&
        !(Array.isArray(val) && val.length === 0)
    );

    // ✅ If no data → go back directly
    if (!hasData) {
      window.history.back();
      return;
    }

    // ⚠️ If user entered something → show confirm
    Modal.confirm({
      title: "Leave this page?",
      icon: <ExclamationCircleOutlined />,
      content: "All your unsaved changes will be lost if you go back.",
      okText: "Yes, go back",
      cancelText: "Stay",
      okButtonProps: { danger: true },
      onOk: () => {
        window.history.back();
      },
    });
  };

  const steps = [
    "Description",
    "Details",
    "Location",
    "Pricing",
    "Amenities",
    "Contact",
    "Images",
  ];

  const stepFields = [
    ["title", "description"],
    ["propertyType", "buildingType", "areaSqFt", "floor", "totalFloors", "propertyAge", "furnished"],
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],
    ["pricingType", "price", "deposit", "negotiable"],
    ["amenities"],
    ["contactName", "phone", "email"],
    ["thumbnail", "images"],
  ];

  // Helper function to validate current step fields including custom validations
  const validateCurrentStep = async () => {
    const fieldsToValidate = stepFields[current];

    try {

      if (current === 1) {
        if (showCustomPropertyType && (!customPropertyType || customPropertyType.trim() === "")) {
          message.destroy();
          message.error("Please enter custom property type");
          return false;
        }
      }

      if (current === 6) {
        if (!uploadedThumb) {
          setThumbnailError(true);
          message.error("Please upload a thumbnail image");
          return false;
        }

        if (!uploadedImages || uploadedImages.length === 0) {
          setImagesError(true);
          message.error("Please upload at least one image");
          return false;
        }

        setThumbnailError(false);
        setImagesError(false);
      }

      if (current === 4) {
        const amenitiesValue = form.getFieldValue?.("amenities");

        if (!amenitiesValue || amenitiesValue.trim() === "") {
          message.destroy();
          message.error("Please add at least one amenity");
          return false;
        }
      }


      // ✅ Only validate if NOT step 4 or 6
      if (current !== 4 && current !== 6) {
        await form.validateFields(fieldsToValidate);
      }
      return true;

    } catch (err) {
      let errorMessage = "Please fill all required fields";

      if (err?.errorFields?.length > 0) {
        errorMessage = err.errorFields[0]?.errors?.[0] || "Please fill all required fields correctly";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      message.destroy();
      message.error(errorMessage);
      return false;
    }
  };

  const next = async () => {
    const isValid = await validateCurrentStep();
    // 🔥 FORCE STOP
    if (!isValid) return;

    setCurrent(current + 1);
    window.scrollTo(0, 0);
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const data = {
        title: values.title,
        description: values.description,
        propertyDetails: {
          propertyType: values.propertyType,
          buildingType: values.buildingType,
          areaSqFt: Number(values.areaSqFt),
          furnished: !!values.furnished,
          floor: Number(values.floor),
          totalFloors: Number(values.totalFloors),
          propertyAge: Number(values.propertyAge),
        },
        location: {
          address: values.address,
          city: values.city,
          locality: values.locality.trim(),
          state: values.state,
          country: values.country,
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
        },
        pricing: {
          type: values.pricingType,
          price: Number(values.price),
          ...(values.pricingType === "rent" && values.deposit ? { deposit: Number(values.deposit) } : {}),
          negotiable: !!values.negotiable,
        },
        amenities: values.amenities
          ? values.amenities.split(",").map((a) => a.trim()).filter(Boolean)
          : [],
        contactDetails: {
          contactName: values.contactName,
          phone: values.phone,
          email: values.email,
        },
      };



      await API.post("/commercial-spaces", {
        ...data,
        thumbnail: uploadedThumb?.url,
        images: uploadedImages.map(img => img.url),
      });

      navigate("/success", {
        state: { type: "commercial" }
      });
      setThumbnailError(false);
      setImagesError(false);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedCustomSelect = e.target.closest(
        ".csv2-custom-select"
      );

      if (!clickedCustomSelect) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);


  return (
    <div className="csv2-page">
      <div className="csv2-container">
        <Card className="csv2-card">
          {/* Card Header - Back button on right side */}
          <div className="csv2-card-header">
            <div className="csv2-header-left" />
            <div className="csv2-header-center">
              <Title className="csv2-title">Add Commercial Space</Title>
            </div>
            <div className="csv2-header-right">
              <button className="csv2-back-btn" onClick={handleBack} type="button">
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Stepper */}
          <div className="csv2-stepper">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`csv2-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
              >
                <div className="csv2-step-line-left" />
                <div className="csv2-step-inner">
                  <div className="csv2-circle">
                    {current > i ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className="csv2-step-label">{step}</span>
                </div>
                <div className="csv2-step-line-right" />
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="csv2-progress-bar">
            <div
              className="csv2-progress-fill"
              style={{ width: `${((current) / (steps.length - 1)) * 100}%` }}
            />
          </div>
          <div className="csv2-progress-label">
            Step {current + 1} of {steps.length} — {steps[current]}
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            validateTrigger="onSubmit"
            className="csv2-form"
          >
            {/* Step 0: Description */}
            <div style={getStepStyle(0)}>
              <Form.Item
                name="title"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Property Title
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter a title for your property" },
                  { min: 5, message: "Title must be at least 5 characters long" },
                  {
                    pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.#&-]*$/,
                    message: "Title must include at least one letter and can contain numbers or symbols like # . & -",
                  },
                ]}
              >
                <Input placeholder="e.g. Premium Office Space in Indiranagar" />
              </Form.Item>

              <Form.Item
                name="description"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Description
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please provide a description" },
                  { min: 20, message: "Description should be at least 20 characters" },
                ]}
              >
                <TextArea rows={5} placeholder="Describe the key features, location advantages, and unique qualities of your commercial space..." />
              </Form.Item>
            </div>

            {/* Step 1: Details */}
            <div style={getStepStyle(1)}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="propertyType"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Property Type
                      </span>
                    }
                    required
                    rules={[{ required: true, message: "Please select a property type" }]}
                  >
                    <div className="csv2-custom-select">
                      <div
                        className="csv2-custom-select-trigger"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown === "propertyType"
                              ? null
                              : "propertyType"
                          )
                        }
                      >
                        {propertyTypeValue
                          ? propertyTypeValue.charAt(0).toUpperCase() +
                          propertyTypeValue.slice(1)
                          : "Select property type"}

                        <span className="csv2-custom-arrow">⌄</span>
                      </div>

                      {openDropdown === "propertyType" && (
                        <div className="csv2-custom-dropdown">
                          {PROPERTY_TYPES.map((item) => (
                            <div
                              key={item}
                              className="csv2-custom-dropdown-item"
                              onClick={() => {
                                form.setFieldValue(
                                  "propertyType",
                                  item
                                );

                                if (item === "other") {
                                  setShowCustomPropertyType(true);
                                } else {
                                  setShowCustomPropertyType(false);
                                  setCustomPropertyType("");
                                }

                                setOpenDropdown(null);
                              }}
                            >
                              {item.charAt(0).toUpperCase() +
                                item.slice(1)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                  {showCustomPropertyType && (
                    <Form.Item
                      name="customPropertyType"
                      label="Enter Custom Property Type"
                      rules={[
                        { required: true, message: "Please enter custom property type" },
                        { min: 2, message: "Property type must be at least 2 characters" },
                        { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" }
                      ]}
                      style={{ marginTop: -8 }}
                    >
                      <Input
                        placeholder="e.g. Restaurant, Hotel, Clinic"
                        value={customPropertyType}
                        onChange={(e) => {
                          setCustomPropertyType(e.target.value);
                          form.setFieldValue("propertyType", e.target.value);
                        }}
                      />
                    </Form.Item>
                  )}
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="buildingType"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Building Name
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the building name" },
                      {
                        pattern: /^(?=.*[A-Za-z0-9])[A-Za-z0-9\s.-]+$/,
                        message: "Building name can include letters, numbers, spaces, . and -",
                      },
                    ]}
                  >
                    <Input placeholder="e.g. Prestige Tech Park" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="floor"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Floor Number
                      </span>
                    }
                    required
                    dependencies={["totalFloors"]}
                    rules={[
                      { required: true, message: "Please enter the floor number" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const totalFloors = getFieldValue("totalFloors");
                          if (value && totalFloors && value > totalFloors) {
                            return Promise.reject(new Error("Floor cannot exceed total floors"));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 3" min={0} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="totalFloors"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Total Floors
                      </span>
                    }
                    required
                    rules={[{ required: true, message: "Please enter total floors" }]}
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="propertyAge"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Property Age (Years)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter property age" },
                      { type: "number", min: 0, message: "Property age cannot be negative" },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 5" min={0} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="areaSqFt"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Area (sq ft)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the area in sq ft" },
                      { type: "number", min: 10, message: "Area must be at least 10 sq ft" },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 1200" min={10} />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item label="Furnished" className="csv2-switch-item">
                    <Form.Item name="furnished" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                    <span className="csv2-switch-label">Mark if the space comes fully or partially furnished</span>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 2: Location */}
            <div style={getStepStyle(2)}>
              <Form.Item
                name="address"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Address
                  </span>
                }
                required
                rules={[{ required: true, message: "Please enter the address" }]}
              >
                <Input placeholder="e.g. 45, 1st Cross Road" />
              </Form.Item>

              <Form.Item
                name="locality"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Locality
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter locality" },
                  { min: 2, message: "Locality must be at least 2 characters" },
                  { max: 100, message: "Locality cannot exceed 100 characters" },
                  {
                    pattern: /^[A-Za-z0-9\s().-]*$/,
                    message:
                      "Locality can only contain letters, numbers, spaces, (), . and -",
                  },
                ]}
              >
                <LocationField
                  form={form}
                  message={message}
                  setAutoDetecting={setAutoDetecting}
                />
              </Form.Item>

              <Form.Item
                name="city"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    City
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the city" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "City name should contain only letters" },
                ]}
              >
                <Input placeholder="e.g. Bengaluru" />
              </Form.Item>



              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="state"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        State
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the State" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "State name should contain only letters" },
                    ]}
                  >
                    <Input placeholder="e.g. Karnataka" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="country"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Country
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the country" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "Country name should contain only letters" },
                    ]}
                  >
                    <Input placeholder="e.g. India" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="pincode"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Pincode
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the pincode" },
                  { pattern: /^[1-9][0-9]{5}$/, message: "Enter a valid 6-digit pincode" },
                ]}
              >
                <Input maxLength={6} placeholder="e.g. 560038" style={{ maxWidth: 200 }} />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="latitude"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Latitude
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Latitude is required" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === "") {
                            return Promise.reject("Latitude is required");
                          }
                          const numValue = Number(value);
                          if (isNaN(numValue)) {
                            return Promise.reject("Enter a valid latitude");
                          }
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1) {
                            return Promise.reject("Only one decimal point allowed");
                          }
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6) {
                            return Promise.reject("Max 6 digits allowed after decimal");
                          }
                          if (numValue < -90 || numValue > 90) {
                            return Promise.reject("Latitude must be between -90 and 90");
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-90}
                      max={90}
                      step={0.000001}
                      placeholder="e.g. 12.971891"
                      onBlur={() => {
                        const val = form.getFieldValue("latitude");
                        if (typeof val === "number" && !isNaN(val)) {
                          form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="longitude"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Longitude
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Longitude is required" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === "") {
                            return Promise.reject("Longitude is required");
                          }
                          const numValue = Number(value);
                          if (isNaN(numValue)) {
                            return Promise.reject("Enter a valid longitude");
                          }
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1) {
                            return Promise.reject("Only one decimal point allowed");
                          }
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6) {
                            return Promise.reject("Max 6 digits allowed after decimal");
                          }
                          if (numValue < -180 || numValue > 180) {
                            return Promise.reject("Longitude must be between -180 and 180");
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-180}
                      max={180}
                      step={0.000001}
                      placeholder="e.g. 77.594563"
                      onBlur={() => {
                        const val = form.getFieldValue("longitude");
                        if (typeof val === "number" && !isNaN(val)) {
                          form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 3: Pricing */}
            <div style={getStepStyle(3)}>
              <Form.Item
                name="pricingType"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Pricing Type
                  </span>
                }
                required
                rules={[{ required: true, message: "Please select a pricing type" }]}
              >
                <div className="csv2-custom-select">
                  <div
                    className="csv2-custom-select-trigger"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "pricingType"
                          ? null
                          : "pricingType"
                      )
                    }
                  >
                    {pricingTypeValue
                      ? pricingTypeValue.charAt(0).toUpperCase() +
                      pricingTypeValue.slice(1)
                      : "Select pricing type"}

                    <span className="csv2-custom-arrow">⌄</span>
                  </div>

                  {openDropdown === "pricingType" && (
                    <div className="csv2-custom-dropdown">
                      {[
                        { label: "Rent", value: "rent" },
                        { label: "Lease", value: "lease" },
                        { label: "Sale", value: "sell" },
                      ].map((item) => (
                        <div
                          key={item.value}
                          className="csv2-custom-dropdown-item"
                          onClick={() => {
                            form.setFieldValue(
                              "pricingType",
                              item.value
                            );

                            setOpenDropdown(null);
                          }}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="price"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Price (₹)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the price" },
                      { type: "number", min: 1, message: "Price must be greater than 0" },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 50000" min={1} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.pricingType !== currentValues.pricingType ||
                      prevValues.price !== currentValues.price
                    }
                  >
                    {({ getFieldValue }) => {
                      const pricingType = getFieldValue("pricingType");
                      if (pricingType === "rent") {
                        return (
                          <Form.Item
                            name="deposit"
                            label={
                              <span className="csv2-label-with-badge">
                                <span className="csv2-required-star">*</span>
                                Deposit (₹)
                              </span>
                            }
                            rules={[
                              { required: true, message: "Please enter the deposit amount" },
                              { type: "number", min: 0, message: "Deposit cannot be negative" },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const price = getFieldValue("price");
                                  if (value !== undefined && price !== undefined && value !== null && value <= price) {
                                    return Promise.reject(new Error("Deposit must be greater than price"));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="e.g. 100000"
                              min={0}
                            />
                          </Form.Item>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Negotiable" className="csv2-switch-item">
                <Form.Item name="negotiable" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
                <span className="csv2-switch-label">Price is open to negotiation</span>
              </Form.Item>
            </div>

            {/* Step 4: Amenities - UPGRADED VERSION */}
            <div style={getStepStyle(4)}>
              <Form.Item
                name="amenities"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Amenities
                  </span>
                }
                validateTrigger="onChange"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || value.trim() === "") {
                        return Promise.reject("Please add at least one amenity");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                getValueFromEvent={(val) => val}
              >
                <AmenitiesInput message={message} />
              </Form.Item>
            </div>

            {/* Step 5: Contact */}
            <div style={getStepStyle(5)}>
              <Form.Item
                name="contactName"
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Contact Person Name
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter contact name" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "Name can only contain letters and basic characters" },
                ]}
              >
                <Input placeholder="e.g. Rajesh Kumar" />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Phone Number
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter a phone number" },
                      { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit Indian phone number" },
                    ]}
                  >
                    <Input maxLength={10} placeholder="e.g. 9876543210" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label={
                      <span className="csv2-label-with-badge">
                        <span className="csv2-required-star">*</span>
                        Email Address
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter your email address" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                  >
                    <Input placeholder="e.g. rajesh@company.com" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 6: Images */}
            <div style={getStepStyle(6)}>
              <Form.Item
                required
                name="thumbnail"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Thumbnail
                    <span className="csv2-badge-primary">Main Image</span>
                  </span>
                }

                rules={[
                  { required: true, message: "Please upload a thumbnail image" },
                  {
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject("Please upload a thumbnail image");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateStatus={thumbnailError ? "error" : ""}
                help={thumbnailError ? "Please upload a thumbnail image" : ""}
              >
                <Upload.Dragger
                  maxCount={1}
                  accept="image/png,image/jpeg"
                  className="csv2-upload"
                  beforeUpload={(file) => {
                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG, JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadThumbnail(file);
                    return false;
                  }}
                  onChange={() => {
                    setThumbnailError(false);
                  }}
                >
                  <div className="csv2-upload-inner">
                    <p className="csv2-upload-title">Upload Thumbnail</p>
                    <p className="csv2-upload-hint">This is the first image users seen · PNG, JPEG</p>
                  </div>
                </Upload.Dragger>
              </Form.Item>

              <Form.Item
                required
                name="images"

                label={
                  <span className="csv2-label-with-badge">
                    <span className="csv2-required-star">*</span>
                    Property Images
                    <span className="csv2-badge-secondary">Up to 5</span>
                  </span>
                }

                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject("Please upload at least one image of the property");
                      }
                      if (value.length > 5) {
                        return Promise.reject("Maximum 5 images allowed");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateStatus={imagesError ? "error" : ""}
                help={imagesError ? "Please upload at least one image of the property" : ""}
              >
                <Upload.Dragger
                  fileList={imageFileList}
                  multiple
                  accept="image/png,image/jpeg"
                  className="csv2-upload"

                  beforeUpload={(file) => {

                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG and JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    // max 5 validation
                    if (imageFileList.length >= 5) {
                      message.error("Maximum 5 images allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadImages([file]);

                    setImagesError(false);

                    return false;
                  }}

                  onRemove={(file) => {
                    removeImage(file.public_id);

                    const updatedList = imageFileList.filter(
                      (img) => img.public_id !== file.public_id
                    );

                    setImageFileList(updatedList);

                    form.setFieldsValue({
                      images: updatedList,
                    });
                  }}
                >
                  {imageFileList.length >= 5 ? null : (
                    <div className="csv2-upload-inner">

                      <p className="csv2-upload-title">
                        Upload Property Images
                      </p>

                      <p className="csv2-upload-hint">
                        Drag & drop images here or click to browse
                      </p>

                      <p className="csv2-upload-hint">
                        PNG, JPEG · Maximum 5 images
                      </p>

                    </div>
                  )}
                </Upload.Dragger>
              </Form.Item>
            </div>

            {/* Navigation Buttons */}
            <div className="csv2-btns">
              {current > 0 && (
                <Button
                  className="csv2-btn-prev"
                  onClick={prev}
                  disabled={autoDetecting}
                >
                  Previous
                </Button>
              )}
              <div className="csv2-btns-right">
                {current < steps.length - 1 && (
                  <Button
                    className="csv2-btn-next"
                    onClick={next}
                    disabled={autoDetecting}
                  >
                    Next
                  </Button>
                )}
                {current === steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      const isValid = await validateCurrentStep();
                      if (!isValid) return;
                      form.submit();
                    }}
                    loading={loading}
                    disabled={loading || uploading}
                    className="csv2-btn-submit"
                  >
                    Submit Listing
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default AddCommercialSpace;